<script lang="ts">
  import { onMount } from 'svelte';
  import { configureDimensionColors } from './lib/charts/dimension-colors';
  import { installTelemetry, record, recordError, setAppAttribute } from './lib/debug/telemetry';
  import { BrowserDataLoader } from './lib/data/loader';
  import { DataStore } from './lib/data/store';
  import type { Manifest } from './lib/data/types';
  import FilterSheet from './lib/filters/FilterSheet.svelte';
  import AppShell from './lib/layout/AppShell.svelte';
  import { AppState } from './lib/state/app.svelte';
  import InlineError from './lib/ui/InlineError.svelte';
  import PageHeader from './lib/ui/PageHeader.svelte';
  import Skeleton from './lib/ui/Skeleton.svelte';
  import AccountsPage from './pages/AccountsPage.svelte';
  import FlowPage from './pages/FlowPage.svelte';
  import HomePage from './pages/HomePage.svelte';
  import NetWorthPage from './pages/NetWorthPage.svelte';
  import TransactionsPage from './pages/TransactionsPage.svelte';

  const loader = new BrowserDataLoader();
  const appState = new AppState();
  let manifest = $state<Manifest | null>(null);
  let store = $state<DataStore | null>(null);
  let coreState = $state<'loading' | 'ready' | 'error'>('loading');
  let coreError = $state('');
  let transactionsState = $state<'idle' | 'loading' | 'ready' | 'error'>('idle');
  let transactionsError = $state('');

  installTelemetry(() => ({
    app: appState.toJSON(),
    core: coreState,
    coreError: coreError || null,
    transactions: transactionsState,
    transactionsError: transactionsError || null,
    revision: manifest?.revision ?? null,
    generatedAt: manifest?.generated_at ?? null,
    rows: store
      ? {
          accounts: store.core.accounts.length,
          groups: store.core.account_groups.length,
          categories: store.core.categories.length,
          accountMonths: store.core.account_month.length,
          transactions: store.transactions.length,
        }
      : null,
  }));

  // Stato di alto livello leggibile da test e script senza timeout: `<html data-money-*>`.
  $effect(() => setAppAttribute('core', coreState));
  $effect(() => setAppAttribute('transactions', transactionsState));
  $effect(() => setAppAttribute('route', appState.route));

  async function bootstrap(): Promise<void> {
    coreState = 'loading';
    coreError = '';
    try {
      const [nextManifest, core] = await Promise.all([loader.loadManifest(), loader.loadCore()]);
      manifest = nextManifest;
      store = new DataStore(core);
      configureDimensionColors([
        {
          scope: 'groups',
          dimensions: store.core.account_groups.map((group) => ({
            key: `group:${group.id}`,
            label: group.name,
          })),
        },
        {
          scope: 'accounts',
          dimensions: store.core.accounts.map((account) => ({
            key: `account:${account.id}`,
            label: account.name,
          })),
        },
        {
          scope: 'expense-categories',
          dimensions: [
            ...store.core.categories
              .filter((category) => category.kind === 'expense')
              .map((category) => ({
                key: `category:${category.id}`,
                label: category.name,
              })),
            { key: 'category:uncategorized', label: 'Senza categoria' },
          ],
        },
        {
          scope: 'income-categories',
          dimensions: store.core.categories
            .filter((category) => category.kind === 'income')
            .map((category) => ({
              key: `category:${category.id}`,
              label: category.name,
            })),
        },
        {
          scope: 'aggregates',
          dimensions: [{ key: 'aggregate:total', label: 'Totale' }],
        },
      ]);
      appState.initialize(store, nextManifest.range);
      coreState = 'ready';
      record('info', 'data.core', `core pronto (${nextManifest.revision})`, {
        accountMonths: core.account_month.length,
      });
    } catch (error) {
      recordError('data.core', error);
      coreError =
        error instanceof Error ? error.message : 'Errore sconosciuto durante il caricamento.';
      coreState = 'error';
    }
  }

  async function loadTransactions(): Promise<void> {
    if (!store || transactionsState === 'loading' || transactionsState === 'ready') return;
    transactionsState = 'loading';
    transactionsError = '';
    try {
      const payload = await loader.loadTransactions();
      store.setTransactions(payload.transactions);
      transactionsState = 'ready';
      record('info', 'data.transactions', `${store.transactions.length} transazioni pronte`);
    } catch (error) {
      recordError('data.transactions', error);
      transactionsError =
        error instanceof Error ? error.message : 'Errore sconosciuto durante il caricamento.';
      transactionsState = 'error';
    }
  }

  function retryTransactions(): void {
    transactionsState = 'idle';
    void loadTransactions();
  }

  $effect(() => {
    if (
      store &&
      (appState.route === 'accounts' ||
        appState.route === 'expenses' ||
        appState.route === 'income' ||
        appState.route === 'transactions')
    ) {
      void loadTransactions();
    }
  });

  onMount(() => {
    document.documentElement.dataset.theme = appState.dark ? 'dark' : 'light';
    const onPopState = () => {
      appState.applyLocation();
      record('info', 'navigation.popstate', `${window.location.pathname}${window.location.search}`);
    };
    window.addEventListener('popstate', onPopState);
    void bootstrap();
    return () => window.removeEventListener('popstate', onPopState);
  });
</script>

{#if coreState === 'ready' && manifest && store}
  <AppShell state={appState} {manifest}>
    {#key `${appState.route}:${appState.historyVersion}`}
      <svelte:boundary
        onerror={(error) =>
          recordError('render', error, { route: appState.route, url: window.location.href })}
      >
        {#if appState.route === 'home'}
          <HomePage {store} {manifest} state={appState} />
        {:else if appState.route === 'net-worth'}
          <NetWorthPage {store} {appState} />
        {:else if appState.route === 'accounts'}
          <AccountsPage
            {store}
            state={appState}
            {transactionsState}
            {transactionsError}
            {retryTransactions}
          />
        {:else if (appState.route === 'expenses' || appState.route === 'income') && transactionsState === 'ready'}
          <FlowPage
            {store}
            {appState}
            kind={appState.route === 'expenses' ? 'expense' : 'income'}
          />
        {:else if appState.route === 'transactions' && transactionsState === 'ready'}
          <TransactionsPage {store} {appState} />
        {:else}
          <main id="main-content" class="page">
            <PageHeader
              title={appState.route === 'transactions'
                ? 'Transazioni'
                : appState.route === 'income'
                  ? 'Entrate'
                  : 'Spese'}
              description="Caricamento dei movimenti granulari in corso. Il bundle viene scaricato una sola volta."
              state={appState}
            />
            {#if transactionsState === 'error'}
              <InlineError message={transactionsError} retry={retryTransactions} />
            {:else}
              <Skeleton height="32rem" label="Caricamento transazioni" />
            {/if}
          </main>
        {/if}
        {#snippet failed(error, reset)}
          <main id="main-content" class="page">
            <InlineError
              title="Questa vista si è interrotta"
              message={error instanceof Error ? error.message : String(error)}
              retryLabel="Ripristina filtri e riprova"
              retry={() => {
                appState.resetFilters();
                reset();
              }}
            />
          </main>
        {/snippet}
      </svelte:boundary>
    {/key}
  </AppShell>
  <FilterSheet {appState} {store} />
{:else}
  <main class="bootstrap" aria-busy={coreState === 'loading'}>
    <div class="bootstrap-brand"><span class="brand-mark">S</span><strong>Money</strong></div>
    {#if coreState === 'error'}
      <InlineError message={coreError} retry={() => void bootstrap()} />
    {:else}
      <div class="bootstrap-loader" role="status">
        <span></span>
        <p>Prepariamo il tuo patrimonio…</p>
      </div>
    {/if}
  </main>
{/if}
